import {
  Box,
  Button,
  InputLeftElement,
  InputRightElement,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React from "react";
import useGetUserRole from "../../hooks/useGetUserRole";
import { FixAndFlipProduct, UserRole } from "../../lib/models/enums";
import {
  adminSelectChannel,
  brokerRetailSelectChannel,
  selectChannel,
  selectDeferredOrig,
  selectFixAndFlipLoanTerm,
  selectGuaranty,
  selectLoanTerm,
  thirdPartyRetailSelectChannel,
} from "../../lib/models/short-term/short-term-form-selects.model";
import { FixAndFlipForm } from "../../lib/models/short-term/short-term-form.model";
import {
  brokerRetailUsers,
  isInternalUser,
  thirdPartyRetailUsers,
} from "../../lib/UserPermissions";
import FormSectionHeader from "../common-quote/FormSectionHeader";
import CheckboxField from "../form-fields/CheckboxField";
import SelectField from "../form-fields/SelectField";
import TextInputField from "../form-fields/TextInputField";
import ModalWrapper from "../ModalWrapper";
import ShowForRole from "../ShowForRole";
import { rateBuyDownSelects } from "../../lib/models/rental/rental-form-selects.model";
import SliderField from "../form-fields/SliderField";
import useQuote from "../../hooks/useQuote";
import { FixAndFlipCalculation } from "../../lib/models/short-term/short-term-calculation.model";
import { formatMoney } from "../../lib/number-transforms";

function LoanTermsForm() {
  const { values, setFieldValue, initialValues } = useFormikContext<FixAndFlipForm>();
  const { channel, loanProduct, isZeroOrigFee, buyUpPercentage } = values;
  const { state } = useQuote();
  const { quoteCalculation } = state;
  const role = useGetUserRole();
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const stfCalc = quoteCalculation as FixAndFlipCalculation;

  React.useEffect(() => {
    if (isZeroOrigFee !== "No" && buyUpPercentage !== 0) {
      setFieldValue("deferredOrigPercent", 0);
    }
  }, [isZeroOrigFee, buyUpPercentage, setFieldValue]);

  const setChannel = (channel: string) => {
    values.channel = channel;
  };

  const getChannelSelectByRole = (role: UserRole) => {
    if (!role) return selectChannel;

    if (role === "Administrator") return adminSelectChannel;

    if (thirdPartyRetailUsers.includes(role))
      return thirdPartyRetailSelectChannel;

    if (brokerRetailUsers.includes(role)) return brokerRetailSelectChannel;

    return selectChannel;
  };

  const getMaxOrigAdjustment = ( loanProduct: string) => {
    switch (loanProduct) {
      case "FixNFlip":
        return 1.75
      default:
        return 2.00
    }
  }

    const getMinOrigAdjustment = ( loanProduct: string) => {
        switch (loanProduct) {
      case "FixNFlip":
        return 0.75
      default:
        return 1.00
    }
  }
  
  React.useEffect(() => {
    if (loanProduct === initialValues.loanProduct) return;
    if (loanProduct === "FixNFlip"){
      setFieldValue("originationFeeAdjustment", 1.25);
      return;
    }
    if (loanProduct === "BridgePlus") {
      setFieldValue("originationFeeAdjustment", 1.5);
      return;
    }
    if (loanProduct === "New Construction") {
      setFieldValue("originationFeeAdjustment", 1.5);
      return;
    }
  }, [loanProduct, setFieldValue, initialValues.loanProduct]);

  return (
    <>
      <FormSectionHeader id="loan-terms" label="Loan Terms" />

      <SimpleGrid
        marginTop="8"
        columns={{ base: 1, lg: 2 }}
        spacing={{ base: 6, md: 8, lg: 8 }}
      >
        {isInternalUser(role) ? (
          <SelectField
            label="Channel"
            name="channel"
            data={getChannelSelectByRole(role)}
            placeholder="Select a Channel"
          />
        ) : (
          setChannel("Broker")
        )}

        <SelectField
          label="Guaranty"
          name="guaranty"
          data={selectGuaranty}
          placeholder="Select a Guaranty"
        />

        <SelectField
          label="Loan Term"
          name="loanTerm"
          data={
            values.loanProduct === "FixNFlip"
              ? selectFixAndFlipLoanTerm
              : selectLoanTerm
          }
          placeholder="Select a loan term"
        />
        {isInternalUser(role) && (
          <TextInputField
            label="Valuation Fee"
            name="appraisalFee"
            type="number"
            formatNumber
            children={<InputLeftElement children="$" />}
          />
        )}

        <TextInputField
          label="Interest Reserves"
          name="iREscrowOverrideMoneyAmt"
          type="number"
          formatNumber
          children={<InputLeftElement children="$" />}
        />

        <TextInputField
          label="Maximum Interest Reserve"
          name="maxInterestReserve"
          type="number"
          formatNumber
          disabled="True"
          isReadOnly
          placeholder={stfCalc?.LoanCosts?.maxInterestReserve && formatMoney(stfCalc?.LoanCosts?.maxInterestReserve)}
          children={<InputLeftElement children="$" />}
        />

        <ShowForRole
          roles={["Wholesale", "Wholesale Manager", "Capital Markets"]}
        >
          <>
            <SelectField
              label="Rate Buy Down Fee"
              name="interestRateAddOn"
              placeholder="Select Rate Buy Down Fee"
              data={rateBuyDownSelects}
            />

            <TextInputField
              label="Fee Adjustment"
              name="processingFee"
              type="number"
              formatNumber
              children={<InputLeftElement children="$" />}
            />

            <TextInputField
              label="Partner Origination Fee"
              name="originationPointsAddOn"
              type="number"
              formatNumber
              children={<InputRightElement children="%" />}
            />
          </>
        </ShowForRole>

        {channel.includes("Broker") && (
          <>
            <TextInputField
              label="Broker Fee"
              name="brokerFee"
              type="number"
              children={<InputRightElement children="%" />}
            />
            {isInternalUser(role) && (
              <TextInputField
                label="Broker Processing Fee"
                name="brokerProcessingFee"
                type="number"
                formatNumber
                children={<InputLeftElement children="$" />}
              />
            )}
          </>
        )}

        {loanProduct !== FixAndFlipProduct["New Construction"] && (
          <SelectField
            label="Deferred Origination Percentage"
            name="deferredOrigPercent"
            data={selectDeferredOrig}
            placeholder="Select a Deferment Percentage"
            disabled={buyUpPercentage || isZeroOrigFee !== "No"}
            helperText={
              <>
                <Button
                  variant="link"
                  marginTop={2}
                  fontSize="xs"
                  onClick={() => {
                    setIsOpen(!isOpen);
                  }}
                  float="right"
                >
                  What does this mean?
                </Button>
                <ModalWrapper
                  isOpen={isOpen}
                  onClose={() => setIsOpen(!isOpen)}
                  title="Explanation of Deferred Origination Fee"
                  size="lg"
                  footer={
                    <Button onClick={() => setIsOpen(!isOpen)}>Close</Button>
                  }
                >
                  <Text>
                    A portion or all of the origination fee may be deferred and
                    paid at the time of loan payoff
                  </Text>
                </ModalWrapper>
              </>
            }
          />
        )}

        <SliderField
          label="Origination Fee"
          name="originationFeeAdjustment"
          defaultValue={1.25}
          step={0.125}
          max={getMaxOrigAdjustment(loanProduct)}
          min={getMinOrigAdjustment(loanProduct)}
          precision={3}
          inputFieldDisable={true}
        />

         <SliderField
          label="Interest Rate Buy Down (+) / Buy Up (-) Fee"
          name="buyUpPercentage"
          defaultValue={0}
          step={0.125}
          max={0.50}
          min={-0.50}
          precision={3}
          inputFieldDisable={true}
        />

        {isInternalUser(role) && (
          <Box>
            <CheckboxField label="Paid Referral?" name="referralFee" />
          </Box>
        )}

        {isInternalUser(role) &&
          values.loanProduct === "BridgePlus" &&
          values.loanPurpose === "Refinance" && (
            <Box>
              <CheckboxField
                label="Internal Refinance?"
                name="internalRefinance"
              />
            </Box>
          )}
      </SimpleGrid>
    </>
  );
}

export default LoanTermsForm;
